import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { PartnerService } from '@feature/dashboard/partners/services/partner.service';
import { PartnerListInterface } from '@feature/dashboard/partners/types/partner-list';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { sortDirection } from '@sharedModule/constants/data-grid.constants';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { HasAccessDirective } from '@sharedModule/directives/has-access.directive';
import { HasPlatformAccessDirective } from '@sharedModule/directives/has-platform-access.directive';
import { MaterialModule } from '@sharedModule/material/material.module';
import { ConfirmationCommonDialogData } from '@sharedModule/models/confirmation-dialog.model';
import { ConfirmationDialogService } from '@sharedModule/services/confirmation-dialog/confirmation-dialog.service';
import { ExcelExportService } from '@sharedModule/services/excel-export/excel-export.service';
import { ReportFilterDialogComponent } from '@sharedModule/components/report-filter-dialog/report-filter-dialog.component';
import { getActiveSort } from '@sharedModule/utils/sort.utils';
import { Subject, Observable, debounceTime, takeUntil } from 'rxjs';
import { RecipientService } from '../../services/recipient.service';
import {
  updateRecipientListFilter,
  deleteRecipient,
  resetRecipientList,
} from '../../store/recipient-list.actions';
import { selectRecipientList } from '../../store/recipient-list.selector';
import { RecipientListInterface } from '../../types/recipient-list';
import {
  defaultRecipientListFilter,
  RecipientListFilter,
} from '../../types/recipient-list-filter';

@Component({
  selector: 'app-recipient-list',
  standalone: true,
  imports: [
    InputModule,
    CommonModule,
    RouterModule,
    MaterialModule,
    HasAccessDirective,
    ReactiveFormsModule,
    HasPlatformAccessDirective,
  ],
  templateUrl: './recipient-list.component.html',
  styleUrl: './recipient-list.component.scss',
})
export class RecipientListComponent implements OnInit, OnDestroy {
  public readonly attrConstant = ATTRIBUTES_CONSTANT.recipients;
  filterForm!: FormGroup;
  displayedColumns: string[] = [
    'id',
    'bene_first_name',
    'benificiary_country',
    'bene_mobile_number',
    'created_at',
    'action',
  ];
  recipientList?: RecipientListInterface[];
  dataSource!: MatTableDataSource<RecipientListInterface>;
  activeSort?: Sort;
  fallBackSort = {
    active: 'created_at',
    direction: sortDirection.desc,
  };
  page = 0;
  total = 0;
  limit = 10;
  subject = new Subject<{
    partner_id: number;
    search: string;
  }>();
  isSuperAdmin!: boolean;
  partnerList!: Observable<PartnerListInterface[]>;
  #destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private partnerService: PartnerService,
    private recipientService: RecipientService,
    private excelExportService: ExcelExportService,
    private authenticationService: AuthenticationService,
    private messageHandlerService: MessageHandlerService,
    private confirmationDialogService: ConfirmationDialogService
  ) {
    this.isSuperAdmin = this.authenticationService.isSuperAdmin();
  }

  ngOnInit(): void {
    this.filterFormInit();
    this.loadData();
    this.dispatchFilter();
    const debounceTimeDuration = 1000;
    this.subject
      .pipe(debounceTime(debounceTimeDuration), takeUntil(this.#destroy$))
      .subscribe(res => {
        const partnerId = this.filterForm.value[this.attrConstant.partner.key];
        this.dispatchFilter({
          count: 10,
          page: 0,
          ...res,
          partner_id: ['', null].includes(partnerId) ? null : [partnerId],
        });
      });
    if (this.isSuperAdmin) this.partnerList = this.partnerService.sharedData$;
  }

  filterFormInit(): void {
    this.filterForm = new FormGroup({
      [this.attrConstant.partner.key]: new FormControl(''),
      [this.attrConstant.search.key]: new FormControl(''),
    });
  }

  loadData(): void {
    this.store
      .select(selectRecipientList)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(data => {
        if (data) {
          this.recipientList = data.rawData;
          this.dataSource = new MatTableDataSource(data.rawData);
          this.page = data.page;
          this.total = data.totalRecords;
          this.limit = data.count;
        }
      });
  }

  applyFilter(): void {
    this.subject.next(this.filterForm.value);
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.dispatchFilter({ ...defaultRecipientListFilter });
  }

  reload(): void {
    this.dispatchFilter();
  }

  onPageChange(pageEvent: PageEvent): void {
    this.dispatchFilter({
      count: pageEvent.pageSize,
      page: pageEvent.pageIndex,
    });
  }

  onSortChange(sortEvent: Sort): void {
    this.activeSort = getActiveSort(sortEvent, this.fallBackSort);
    this.dispatchFilter({
      sort_key: this.activeSort.active,
      sort_by: this.activeSort.direction,
    });
  }

  dispatchFilter(recipientListFilter?: RecipientListFilter): void {
    this.store.dispatch(
      updateRecipientListFilter({ filter: recipientListFilter ?? {} })
    );
  }

  export(): void {
    this.excelExportService.exportToExcel(this.recipientList!, 'recipients');
  }

  openReport(): void {
    const partnerId = this.filterForm.value[this.attrConstant.partner.key];
    this.dialog.open(ReportFilterDialogComponent, {
      width: '640px',
      data: {
        type: 'beneficiary',
        partnerId: ['', null].includes(partnerId) ? null : partnerId,
      },
    });
  }

  async onDeleteRecipient(data: RecipientListInterface): Promise<void> {
    const alertCommonDialogData: ConfirmationCommonDialogData = {
      title: '',
      message: `Are you sure you're going to delete this recipient?`,
      submitButtonText: 'Confirm',
    };
    const isConfirmed =
      await this.confirmationDialogService.openConfirmationDialog(
        alertCommonDialogData
      );
    if (isConfirmed) {
      this.recipientService
        .deleteRecipient({
          partner_id: data.partner_id,
          sender_id: data.sender_id,
          bene_id: data.bene_id,
        })
        .pipe(takeUntil(this.#destroy$))
        .subscribe({
          next: x => {
            if (x.success) {
              this.messageHandlerService.show(x.message);
              this.store.dispatch(
                deleteRecipient({
                  partner_id: data.partner_id,
                  sender_id: data.sender_id,
                  bene_id: data.bene_id,
                })
              );
            }
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.store.dispatch(resetRecipientList());
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
